'use client'

import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import { format } from "date-fns";
import { Log } from "../../lib/definitions";
import { getGraphColor, getLevelText } from "../../lib/utils";
import LineChart from './lineChart';

import styles from "./page.module.css";

const DATE_PADDING = 1000 * 60 * 60
const RATING_PADDING = 100

type Props = {
  logs: Log[]
  rating: number
}
const RatingChart = ({ logs, rating }: Props) => {
  const sortedLogs = [...logs].sort((a, b) => new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf())
  const refinedLogs = sortedLogs.map((log, index) => {
    if (index === 0) {
      return {
        ...log,
        changedRating: 0
      }
    }
    return {
      ...log,
      changedRating: log.rating - sortedLogs[index - 1].rating
    }
  })
  const minDate = sortedLogs.length > 0 ? new Date(refinedLogs[0].createdAt).valueOf() - DATE_PADDING : new Date().valueOf()
  const maxDate = sortedLogs.length > 0 ? new Date(sortedLogs[sortedLogs.length - 1].createdAt).valueOf() + DATE_PADDING : new Date().valueOf()

  const minRating = Math.floor((Math.min(...refinedLogs.map((log) => log.rating)) - RATING_PADDING) / RATING_PADDING) * RATING_PADDING
  const maxRating = Math.ceil((Math.max(...refinedLogs.map((log) => log.rating)) + RATING_PADDING) / RATING_PADDING) * RATING_PADDING

  const data: ChartData<'line'> = {
    labels: refinedLogs.map((log) => log.createdAt),
    datasets: [
      {
        backgroundColor: getGraphColor(rating),
        borderColor: getGraphColor(rating),
        borderWidth: 2,
        hoverBackgroundColor: 'rgb(255, 255, 255)',
        hoverBorderColor: getGraphColor(rating),
        hoverBorderWidth: 4,
        data: refinedLogs.map((log) => {
          return {
            x: new Date(log.createdAt).valueOf(),
            y: log.rating,
            dy: log.changedRating,
            problemId: log.problemId,
            problemLevel: log.problemLevel,
            problemName: log.problemName
          }
        }),
      },
    ],
  };
  
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          callback: (value, index, values) => {
            const date = new Date(value)
            const diff = values.length > 0 ? (values[values.length - 1].value - values[0].value) / (1000 * 60 * 60 * 24) : 0
            if (diff < 1 * 7) {
              return format(date, 'MMM dd Haa')
            } else if (diff < 30 * 3) {
              return format(date, 'MMM dd yyyy')
            } else if (diff < 365) {
              return format(date, 'MMM yyyy')
            } else {
              return format(date, 'yyyy')
            }
          },
        },
        min: minDate,
        max: maxDate
      },
      y: {
        min: minRating,
        max: maxRating,
        ticks: {
          callback: (value) => {
            if (typeof value === 'string' && Math.floor(Number(value)) === Number(value)) {
              return value
            }
            if (typeof value === 'number' && Math.floor(value) === value) {
              return value
            }
          }
        }
      }
    },
    plugins: {
      tooltip: {
        displayColors: false,
        callbacks: {
          beforeLabel: (context: TooltipItem<"line"> & { raw: { problemId: number, problemLevel: number, problemName: string } }) => {
            const { problemId, problemLevel, problemName } = context.raw
            return `[${getLevelText(problemLevel)}] ${problemId} ${problemName}`
          },
          label: (context: TooltipItem<"line"> & { raw: { y: number, dy: number } }) => {
            const { y: rating, dy: ratingDiff } = context.raw
            const flag = ratingDiff < 0 ? '-' : '+'
            const changed = Math.abs(ratingDiff)
            return `${rating} (${flag}${changed})`
          }
        }
      },
      zoom: {
        limits: {
          x: {
            min: minDate,
            max: maxDate,
          },
          y: {
            min: minRating,
            max: maxRating,
          }
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'xy',
        },
        pan: {
          enabled: true,
          mode: 'xy',
        },
      }
    },
    layout: {
      padding: 0
    }
  };

  return (
    <div className={styles.ratingChart}>
      <LineChart data={data} options={options} />
    </div>
  )
}

export default RatingChart